/**
 * Initial Load API Client
 * REST API client for initial load status and management.
 * @packageDocumentation
 */

import { fetchJsonWithAuth } from './fetch-with-auth';

// ============================================================================
// Types
// ============================================================================

/**
 * Load state for a single mapping.
 */
export interface MappingLoadState {
  lastJobId: string | null;
  activeJobId: string | null;
  lastLoadedAt: string | null;
  activeProgress: number | null;
  activeTotalRows: number | null;
  activeProcessedRows: number | null;
  lastJobStatus:
    | 'pending'
    | 'running'
    | 'paused'
    | 'completed'
    | 'completed_with_errors'
    | 'failed'
    | 'cancelled'
    | null;
}

/**
 * Response from the status endpoint.
 */
export interface InitialLoadStatusResponse {
  /** Load state per mapping, keyed by the mapping id as a string; a mapping with no job is absent */
  statuses: Record<string, MappingLoadState>;
}

/**
 * Request body for triggering an initial load.
 */
export interface TriggerInitialLoadRequest {
  tables?: string[];
  /** Id of the mapping to load */
  mappingId: number;
  config?: {
    dryRun?: boolean;
    chunkSize?: number;
    concurrency?: number;
  };
}

/**
 * Response from the trigger endpoint.
 */
export interface TriggerInitialLoadResponse {
  jobId: string;
  status: 'pending';
  createdAt: string;
  mappingId: number;
  /** The mapping key, as a label */
  mappingKey: string;
}

/**
 * Response from the cancel endpoint.
 */
export interface CancelResponse {
  jobId: string;
  message: string;
}

/**
 * Response from the resume endpoint.
 */
export interface ResumeResponse {
  jobId: string;
  message: string;
  status: 'paused' | 'failed';
  executionName: string | null;
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetches the initial load status for mappings.
 *
 * @param { number[] } mappingIds Optional mapping ids to filter.
 * @returns { Promise<InitialLoadStatusResponse> } The status response.
 */
export const fetchInitialLoadStatus = async (mappingIds?: number[]): Promise<InitialLoadStatusResponse> => {
  const params = mappingIds?.length ? `?mappingIds=${encodeURIComponent(mappingIds.join(','))}` : '';

  return fetchJsonWithAuth<InitialLoadStatusResponse>(`/api/initial-load/status${params}`);
};

/**
 * Triggers an initial load for a mapping.
 *
 * @param { TriggerInitialLoadRequest } body The trigger request.
 * @returns { Promise<TriggerInitialLoadResponse> } The trigger response.
 */
export const triggerInitialLoad = async (body: TriggerInitialLoadRequest): Promise<TriggerInitialLoadResponse> =>
  fetchJsonWithAuth<TriggerInitialLoadResponse>('/api/initial-load/trigger', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/**
 * Cancels a running initial load job.
 *
 * @param { string } jobId The job identifier.
 * @returns { Promise<CancelResponse> } The cancel response.
 */
export const cancelInitialLoad = async (jobId: string): Promise<CancelResponse> =>
  fetchJsonWithAuth<CancelResponse>(`/api/initial-load/${encodeURIComponent(jobId)}/cancel`, {
    method: 'POST',
  });

/**
 * Resumes a paused or failed initial load job from its last checkpoint.
 *
 * @param { string } jobId The job identifier.
 * @returns { Promise<ResumeResponse> } The resume response.
 */
export const resumeInitialLoad = async (jobId: string): Promise<ResumeResponse> =>
  fetchJsonWithAuth<ResumeResponse>(`/api/initial-load/${encodeURIComponent(jobId)}/resume`, {
    method: 'POST',
  });
